// ============================================================================
// PÁGINA: DETALHES DA NOTÍCIA - PORTAL PÚBLICO
// ============================================================================
// Arquivo: src/pages/NoticiaDetalhes.tsx
// UPDATED: 2026-04-07 — galeria de imagens renderizada após o conteúdo
// ============================================================================

import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Calendar, Clock, Eye, Heart, Share2, Tag, User,
  TrendingUp, Loader2, AlertCircle, Facebook, Twitter, Linkedin,
  Link as LinkIcon, Globe, ExternalLink, Image, X,
} from "lucide-react";
import { API_BASE } from '../utils/apiConfig';

type CategoriaNoticia =
  | "announcement" | "event" | "tutorial"
  | "industry_news" | "company_news" | "member_spotlight";

interface Noticia {
  id: string;
  title: string;
  subtitle?: string;
  summary: string;
  content: string;
  slug: string;
  category: CategoriaNoticia;
  featuredImageUrl?: string;
  externalUrl?: string;
  publishedAt: string;
  viewsCount: number;
  likesCount: number;
  metadata?: {
    readingTime?: number;
    galleryImages?: string[];
  };
  author?: { id: string; name: string };
  tags?: string[];
  relatedNews?: Array<{
    id: string; title: string; slug: string; featuredImageUrl?: string;
  }>;
}

const categoriasMap: Record<CategoriaNoticia, string> = {
  announcement: "Comunicado", event: "Evento", tutorial: "Tutorial",
  industry_news: "Notícia", company_news: "Empresa", member_spotlight: "Destaque",
};

const categoriasCores: Record<CategoriaNoticia, { bg: string; text: string; border: string }> = {
  announcement:    { bg:"bg-blue-50",   text:"text-blue-700",   border:"border-blue-200" },
  event:           { bg:"bg-purple-50", text:"text-purple-700", border:"border-purple-200" },
  tutorial:        { bg:"bg-green-50",  text:"text-green-700",  border:"border-green-200" },
  industry_news:   { bg:"bg-orange-50", text:"text-orange-700", border:"border-orange-200" },
  company_news:    { bg:"bg-indigo-50", text:"text-indigo-700", border:"border-indigo-200" },
  member_spotlight:{ bg:"bg-amber-50",  text:"text-amber-700",  border:"border-amber-200" },
};

const formatarDataBR = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { day:"2-digit", month:"long", year:"numeric" });

// ─── Lightbox simples ─────────────────────────────────────────────────────────
const Lightbox: React.FC<{ images: string[]; startIndex: number; onClose: () => void }> = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
  const next = () => setIdx(i => (i + 1) % images.length);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 transition"><X className="w-8 h-8" /></button>
      <button onClick={e => { e.stopPropagation(); prev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 text-4xl font-bold transition select-none">‹</button>
      <img src={images[idx]} alt={`img-${idx}`}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
        onClick={e => e.stopPropagation()} />
      <button onClick={e => { e.stopPropagation(); next(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 text-4xl font-bold transition select-none">›</button>
      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        {idx + 1} / {images.length}
      </p>
    </div>
  );
};

// ─── Galeria de imagens ────────────────────────────────────────────────────────
const Galeria: React.FC<{ images: string[] }> = ({ images }) => {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  if (!images.length) return null;

  // Layout responsivo: 1 imagem = full width | 2 = 2 cols | 3+ = masonry 3 cols
  const gridClass = images.length === 1
    ? 'grid-cols-1'
    : images.length === 2
    ? 'grid-cols-2'
    : 'grid-cols-2 md:grid-cols-3';

  return (
    <section className="my-10">
      <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
        <Image className="w-5 h-5 text-amber-600" /> Galeria de Imagens
        <span className="text-sm font-normal text-gray-400">({images.length})</span>
      </h2>
      <div className={`grid ${gridClass} gap-3`}>
        {images.map((url, i) => (
          <div key={i}
            className={`overflow-hidden rounded-2xl border border-gray-200 cursor-pointer group
              ${images.length === 1 ? 'aspect-video' : 'aspect-square'}`}
            onClick={() => setLightboxIdx(i)}>
            <img src={url} alt={`Imagem ${i + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-2">Clique em uma imagem para ampliar</p>

      {lightboxIdx !== null && (
        <Lightbox images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </section>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────
const NoticiaDetalhes: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [noticia, setNoticia]         = useState<Noticia | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [liked, setLiked]             = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    const fetchNoticia = async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${API_BASE}/news/${slug}`);
        if (!res.ok) throw new Error("Notícia não encontrada");
        const data = await res.json();
        setNoticia(data);
      } catch (err: any) { setError(err.message ?? "Erro ao carregar notícia"); }
      finally { setLoading(false); }
    };
    if (slug) fetchNoticia();
  }, [slug]);

  const handleLike = async () => {
    if (!noticia || liked) return;
    try {
      const res = await fetch(`${API_BASE}/news/${noticia.id}/like`, { method:"POST" });
      if (res.ok) {
        const data = await res.json();
        setNoticia(prev => prev ? { ...prev, likesCount: data.likesCount } : null);
        setLiked(true);
      }
    } catch { /* silencioso */ }
  };

  const handleShare = async () => {
    if (!noticia) return;
    try { await fetch(`${API_BASE}/news/${noticia.id}/share`, { method:"POST" }); } catch { /* silencioso */ }
    setShowShareMenu(prev => !prev);
  };

  const shareUrl  = window.location.href;
  const shareText = noticia?.title ?? "";

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 flex items-center justify-center">
      <Loader2 className="w-12 h-12 text-amber-600 animate-spin" />
    </div>
  );

  if (error || !noticia) return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">
      <div className="container mx-auto px-6 py-20">
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-12 text-center max-w-2xl mx-auto">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-red-900 mb-2">{error ?? "Notícia não encontrada"}</h2>
          <Link to="/noticias" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-all">
            <ArrowLeft className="w-5 h-5" /> Voltar para Notícias
          </Link>
        </div>
      </div>
    </div>
  );

  const catConfig    = categoriasCores[noticia.category];
  const galleryImages = noticia.metadata?.galleryImages ?? [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">

      {/* BARRA SUPERIOR */}
      <section className="bg-white border-b-2 border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/noticias" className="inline-flex items-center gap-2 text-gray-700 hover:text-amber-700 font-bold transition-colors">
            <ArrowLeft className="w-5 h-5" /> Voltar para Notícias
          </Link>
          {noticia.externalUrl && (
            <a href={noticia.externalUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl font-semibold text-sm hover:bg-emerald-100 transition-all">
              <Globe className="w-4 h-4" /> Ver fonte original <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </section>

      <article className="container mx-auto px-6 py-12 max-w-5xl">

        {/* IMAGEM DESTAQUE */}
        {noticia.featuredImageUrl && (
          <div className="mb-8 rounded-3xl overflow-hidden border-2 border-gray-200 shadow-2xl">
            <img src={noticia.featuredImageUrl} alt={noticia.title} className="w-full h-[500px] object-cover" />
          </div>
        )}

        {/* CATEGORIA, DATA, META */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold border-2 ${catConfig.bg} ${catConfig.text} ${catConfig.border}`}>
              {categoriasMap[noticia.category]}
            </span>
            <span className="text-gray-500 flex items-center gap-2 text-sm"><Calendar className="w-4 h-4" />{formatarDataBR(noticia.publishedAt)}</span>
            {noticia.metadata?.readingTime && (
              <span className="text-gray-500 flex items-center gap-2 text-sm"><Clock className="w-4 h-4" />{noticia.metadata.readingTime} min de leitura</span>
            )}
            {galleryImages.length > 0 && (
              <span className="text-gray-500 flex items-center gap-2 text-sm"><Image className="w-4 h-4 text-indigo-500" />{galleryImages.length} imagens</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-gray-600 text-sm"><Eye className="w-4 h-4" />{noticia.viewsCount.toLocaleString()}</span>
            <button onClick={handleLike} disabled={liked}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${liked ? "bg-red-100 text-red-700 cursor-not-allowed" : "bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700"}`}>
              <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {noticia.likesCount}
            </button>
            <div className="relative">
              <button onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-100 text-blue-700 font-bold text-sm hover:bg-blue-200 transition-all">
                <Share2 className="w-4 h-4" /> Compartilhar
              </button>
              {showShareMenu && (
                <div className="absolute right-0 mt-2 bg-white rounded-xl border-2 border-gray-200 shadow-xl p-3 min-w-[200px] z-10">
                  {[
                    { href:`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, icon:<Facebook className="w-5 h-5 text-blue-600" />, label:"Facebook" },
                    { href:`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, icon:<Twitter className="w-5 h-5 text-blue-400" />, label:"Twitter" },
                    { href:`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, icon:<Linkedin className="w-5 h-5 text-blue-700" />, label:"LinkedIn" },
                  ].map(s => (
                    <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-700 font-semibold transition-all text-sm">
                      {s.icon} {s.label}
                    </a>
                  ))}
                  <button onClick={() => { navigator.clipboard.writeText(shareUrl); alert("Link copiado!"); }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-700 font-semibold transition-all text-sm w-full">
                    <LinkIcon className="w-5 h-5 text-gray-600" /> Copiar link
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* TÍTULO */}
        <h1 className="text-5xl font-black text-gray-900 mb-4 leading-tight">{noticia.title}</h1>

        {/* SUBTÍTULO */}
        {noticia.subtitle && <p className="text-2xl text-gray-700 font-semibold mb-6">{noticia.subtitle}</p>}

        {/* AUTOR */}
        {noticia.author && (
          <div className="flex items-center gap-3 mb-6 pb-6 border-b-2 border-gray-200">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900">{noticia.author.name}</p>
              <p className="text-sm text-gray-500">Autor</p>
            </div>
          </div>
        )}

        {/* LINK EXTERNO */}
        {noticia.externalUrl && (
          <a href={noticia.externalUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 px-5 py-3 mb-6 bg-emerald-50 border-2 border-emerald-200 rounded-xl text-emerald-700 font-semibold hover:bg-emerald-100 transition-all group">
            <Globe className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1 truncate text-sm">{noticia.externalUrl}</span>
            <ExternalLink className="w-4 h-4 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
          </a>
        )}

        {/* RESUMO */}
        <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-xl mb-8">
          <p className="text-lg text-gray-700 leading-relaxed italic">{noticia.summary}</p>
        </div>

        {/* CONTEÚDO */}
        <div className="prose prose-lg max-w-none mb-8 text-gray-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: noticia.content }} />

        {/* ── GALERIA DE IMAGENS ── */}
        <Galeria images={galleryImages} />

        {/* TAGS */}
        {noticia.tags && noticia.tags.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-12">
            {noticia.tags.map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-all text-sm">
                <Tag className="w-4 h-4" />#{tag}
              </span>
            ))}
          </div>
        )}

        {/* NOTÍCIAS RELACIONADAS */}
        {noticia.relatedNews && noticia.relatedNews.length > 0 && (
          <section className="mt-16 pt-12 border-t-2 border-gray-200">
            <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-amber-600" /> Notícias Relacionadas
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {noticia.relatedNews.map(related => (
                <Link key={related.id} to={`/noticias/${related.slug}`}
                  className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-all group">
                  <div className="h-40 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center overflow-hidden">
                    {related.featuredImageUrl ? (
                      <img src={related.featuredImageUrl} alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    ) : <Tag className="w-12 h-12 text-gray-400" />}
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-black text-gray-900 line-clamp-2 group-hover:text-amber-700 transition-colors">
                      {related.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default NoticiaDetalhes;